import { ProbationFormState, RatingProps } from "../../ProbationAssessmentForm";
import {
  FormErrors,
  required,
  requiredIf,
  requiredSelect,
  validateField,
  validateForm,
  ValidationSchema,
} from "../validation-engine/ValidationEngine";

export type ProbationFeedbackFormProps = FormErrors<ProbationFormState>;

// ─── Field Labels ─────────────────────────────────────────────────────────────
const fieldLabels: Partial<Record<keyof ProbationFormState, string>> = {
  employeeName: "Employee name",
  employeeCode: "Employee code",
  dateOfAppointment: "Date of appointment",
  designation: "Designation",
  function: "Function",
  periodOfProbation: "Period of probation",
  location: "Location",
  dueForConfirmation: "Due for confirmation",
  attendance: "Attendance",
  finalDecision: "Final decision",
  extensionReason: "Extension reason",
  terminationReason: "Termination reason",
  terminationDate: "Termination date",
  confirmationRemarks: "Confirmation remarks",
  managerName: "Manager name",
  managerSignature: "Manager signature",
  dateManagerSign: "Manager sign date",
  hrName: "HR name",
  hrSignature: "HR signature",
  dateHrSign: "HR sign date",
};

const ratingLabels: Partial<Record<keyof RatingProps, string>> = {
  abilityToLearn: "Ability to learn",
  industry: "Industry",
  initiative: "Initiative",
  cooperativeness: "Cooperativeness",
  accuracy: "Accuracy",
  abilityToSupervise: "Ability to supervise",
  knowledgeOfWork: "Knowledge of work",
};

// ─── Ratings validation (separate, outside engine) ────────────────────────
export const validateRatings = (
  ratings: RatingProps,
): Partial<Record<keyof RatingProps, string>> => {
  const errors: Partial<Record<keyof RatingProps, string>> = {};
  const keys: (keyof RatingProps)[] = [
    "abilityToLearn",
    "industry",
    "initiative",
    "cooperativeness",
    "accuracy",
    "abilityToSupervise",
    "knowledgeOfWork",
  ];

  keys.forEach((key) => {
    if (!ratings[key]) errors[key] = "Please select an option.";
  });

  return errors;
};

export const probationSchema: ValidationSchema<ProbationFormState> = {
  employeeName: [required()],
  // employeeCode: [required()],
  dateOfAppointment: [required()],
  designation: [required()],
  function: [required()],
  periodOfProbation: [required()],
  location: [required()],
  dueForConfirmation: [required()],
  attendance: [requiredSelect()],
  // ratings handled separately
  finalDecision: [required()],
  extensionReason: [
    requiredIf<ProbationFormState>((data) => data?.finalDecision === "A"),
  ],
  terminationReason: [
    requiredIf<ProbationFormState>((data) => data?.finalDecision === "B"),
  ],
  terminationDate: [
    requiredIf<ProbationFormState>(
      (data) => data?.finalDecision === "B",
      "Please select a date.",
    ),
  ],
  confirmationRemarks: [
    requiredIf<ProbationFormState>((data) => data?.finalDecision === "C"),
  ],
  managerName: [required()],
  managerSignature: [required()],
  dateManagerSign: [required()],
  hrName: [required()],
  // hrSignature: [required()],
  dateHrSign: [required()],
};

export const validateInductionField = (
  key: keyof ProbationFormState,
  value: string,
): string => validateField(probationSchema[key] ?? [], value);

export const validateProbationForm = (
  formData: ProbationFormState,
): ProbationFeedbackFormProps => {
  const errors: ProbationFeedbackFormProps = validateForm(
    formData,
    probationSchema,
  );

  // requiredIf fields need formData context — evaluate separately
  const conditionalFields: (keyof ProbationFormState)[] = [
    "extensionReason",
    "terminationReason",
    "terminationDate",
    "confirmationRemarks",
  ];

  conditionalFields.forEach((key) => {
    const rules = probationSchema[key] ?? [];
    for (const rule of rules) {
      const error = (rule as any)(String(formData[key] ?? ""), formData);
      if (error) {
        (errors as any)[key] = error;
        break;
      }
    }
  });

  return errors;
};

export const getFirstError = (
  formErrors: ProbationFeedbackFormProps,
  ratingErrors: Partial<Record<keyof RatingProps, string>>,
): string | null => {
  const firstFormKey = Object.keys(formErrors)[0] as
    | keyof ProbationFormState
    | undefined;
  if (firstFormKey)
    return `${fieldLabels[firstFormKey] ?? firstFormKey} is required`;

  const firstRatingKey = Object.keys(ratingErrors)[0] as
    | keyof RatingProps
    | undefined;
  if (firstRatingKey)
    return `${ratingLabels[firstRatingKey] ?? firstRatingKey} is required`;

  return null;
};

// export const validateProbationForm = (
//   formData: ProbationFormState,
// ): ProbationFeedbackFormProps => validateForm(formData, probationSchema);

// import { ProbationFormState } from "../../ProbationAssessmentForm";
// import {
//   required,
//   requiredSelect,
//   validateForm,
//   FormErrors,
//   ValidationSchema,
//   requiredIf,
// } from "../validation-engine/ValidationEngine";

// // ─── FormErrors is now just this one line ─────────────────────────────────────
// export type ProbationFeedbackFormProps = FormErrors<ProbationFormState>;

// // ─── Schema ───────────────────────────────────────────────────────────────────
// export const probationSchema: ValidationSchema<ProbationFormState> = {
//   employeeName: [required()],
//   employeeCode: [required()],
//   dateOfAppointment: [required()],
//   designation: [required()],
//   function: [required()],
//   periodOfProbation: [required()],
//   location: [required()],
//   dueForConfirmation: [required()],

//   attendance: [requiredSelect()],
//   ratings: {
//     abilityToLearn: [requiredSelect()],
//     industry: [requiredSelect()],
//     initiative: [requiredSelect()],
//     cooperativeness: [requiredSelect()],
//     accuracy: [requiredSelect()],
//     abilityToSupervise: [requiredSelect()],
//     knowledgeOfWork: [requiredSelect()],
//   },

//   finalDecision: [required()],
//   extensionReason: [
//     requiredIf<ProbationFormState>((data) => data?.finalDecision === "A"),
//   ],

//   terminationReason: [
//     requiredIf<ProbationFormState>((data) => data?.finalDecision === "B"),
//   ],
//   terminationDate: [
//     requiredIf<ProbationFormState>((data) => data?.finalDecision === "B"),
//   ],

//   confirmationRemarks: [
//     requiredIf<ProbationFormState>((data) => data?.finalDecision === "C"),
//   ],

//   managerName: [required()],
//   managerSignature: [required()],
//   dateManagerSign: [required()],
//   hrName: [required()],
//   hrSignature: [required()],
//   dateHrSign: [required()],
// };

// //     abilityToLearn: string;
// // industry: string;
// // initiative: string;
// // cooperativeness: string;
// // accuracy: string;
// // abilityToSupervise: string;
// // knowledgeOfWork: string;
// //   },
// // extensionReason: [required()],
// // terminationReason: [required()],
// // terminationDate: [required()],
// // confirmationRemarks: [required()],
